'use client';

import type { Blog, BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Pagination } from '../shared/pagination';
import { BlogCard } from './blog-card';

const ALL_CATEGORIES_TAB = 'all';

export const BlogsList = ({
  locale,
  categories,
  initialBlogs,
  listHref,
}: {
  locale: string;
  categories: BlogCategory[];
  initialBlogs?: Awaited<ReturnType<typeof fetchBlogs>>;
  listHref?: string;
}) => {
  const { t } = useTranslations();

  const [activeTab, setActiveTab] = useState(ALL_CATEGORIES_TAB);
  const tabsId = useId();
  const panelId = `${tabsId}-panel`;
  const activeTabId = activeTab === ALL_CATEGORIES_TAB ? undefined : Number(activeTab);
  const initialPage = activeTab === ALL_CATEGORIES_TAB ? (initialBlogs?.meta.current_page ?? 1) : 1;

  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, activeTab, locale, initialPage],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          page: pageParam,
          results: 9,
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
        },
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage.meta.current_page < lastPage.meta.last_page) {
        return lastPage.meta.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: initialPage,
    initialData:
      activeTab === ALL_CATEGORIES_TAB && initialBlogs
        ? { pages: [initialBlogs], pageParams: [initialPage] }
        : undefined,
    staleTime: 60_000,
  });

  const blogs = blogsQuery.data?.pages.flatMap(page => page.data) ?? [];
  const lastPage = blogsQuery.data?.pages.at(-1)?.meta.current_page;
  const nextPageHref =
    activeTab === ALL_CATEGORIES_TAB && listHref && lastPage
      ? `${listHref}?page=${lastPage + 1}`
      : undefined;

  return (
    <>
      <Tabs
        value={activeTab}
        defaultValue={activeTab}
        onValueChange={value => setActiveTab(value)}
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 sm:gap-8 md:gap-10 lg:gap-12',
        )}
      >
        <TabsList
          className={cn(
            'hide-scroll-bar',
            'flex h-fit w-full items-center justify-start gap-2 overflow-x-auto rounded-none bg-transparent p-0 sm:gap-4 lg:gap-6',
          )}
        >
          <TabsTrigger
            id={`${tabsId}-trigger-${ALL_CATEGORIES_TAB}`}
            aria-controls={panelId}
            value={ALL_CATEGORIES_TAB}
            className={cn(
              'ea-universal-tabs__tab-trigger',
              'flex h-10 items-center justify-center whitespace-nowrap rounded-[50px] px-6 text-sm transition-all sm:h-11 sm:text-base lg:h-[50px]',
            )}
          >
            {t('text633')}
          </TabsTrigger>
          {categories.map(category => (
            <TabsTrigger
              id={`${tabsId}-trigger-${category.id}`}
              aria-controls={panelId}
              value={String(category.id)}
              key={category.id}
              className={cn(
                'ea-universal-tabs__tab-trigger',
                'flex h-10 items-center justify-center whitespace-nowrap rounded-[50px] px-6 text-sm transition-all sm:h-11 sm:text-base lg:h-[50px]',
              )}
            >
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent id={panelId} aria-labelledby={`${tabsId}-trigger-${activeTab}`} value={activeTab} className="w-full pr-6">
          {blogsQuery.error && <p className="pt-8 ">{t('text2364')}</p>}
          {blogsQuery.isSuccess && blogs.length === 0 && (
            <p className="w-full text-center font-medium">{t('text752')}</p>
          )}
          {/* {blogsQuery.isLoading && <p className="pt-8 ">Načítavam...</p>} */}
          {blogsQuery.isSuccess && blogs.length > 0 && (
            <div className="grid w-full grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3 xl:gap-6">
              {blogs.map(blog => (
                <BlogCard key={blog.id} blog={blog} headingTag="h2" />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      <Pagination
        query={blogsQuery}
        currentLength={blogs.length}
        nextPageHref={nextPageHref}
        previousPageHref={
          activeTab === ALL_CATEGORIES_TAB && listHref && initialPage > 1
            ? `${listHref}${initialPage > 2 ? `?page=${initialPage - 1}` : ''}`
            : undefined
        }
      />
    </>
  );
};
